import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, Trash2, Loader2, RotateCcw } from 'lucide-react';
import {
  TeamMembersWrapper,
  TeamMembersContainer,
  PageHeader,
  HeaderLeft,
  Title,
  Description,
  InviteUserButton,
  TableCard,
  TableToolbar,
  SearchSlot,
  MemberNameRow,
  MemberName,
  EmailText,
  JoinedText,
  RowActionsGroup,
  RowActionButton,
} from './TeamMembers.styles';

import SearchBar from '../Tickets/components/SearchBar/SearchBar';
import { Table } from '../../../components/uielements/table';
import {
  ActionButtons,
  OutlineButon,
  EmptyState,
  RetryButton,
  SpinWrapper,
} from '../../../settings/appStyle/global.style';
import { useTeamMembersQuery, useDeleteUser } from './hooks/useTeamMembers';
import InviteUserModal from './modals/InviteUserModal';
import { useAuth } from '../../../hooks/useAuth';

const PAGE_SIZE = 10;

const formatDate = (isoString) => (isoString ? new Date(isoString).toLocaleDateString() : '—');

const TeamMembers = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const isRootUser = userData?.rootUser === 1;

  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const { data, isLoading, isError, refetch, isFetching } = useTeamMembersQuery({
    page: page,
    pageSize: PAGE_SIZE,
  });

  const deleteUserMutation = useDeleteUser({
    onSuccess: () => setDeletingUserId(null),
    onError: (err) => {
      setDeletingUserId(null);
      window.alert(`Failed to remove user${err?.message ? `: ${err.message}` : '.'}`);
    },
  });

  const users = data?.users ?? [];
  const total = data?.totalUsers ?? 0;

  const filteredUsers = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (user) =>
        (user.username || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query)
    );
  }, [users, searchValue]);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPage(0);
  };

  const handleDashboard = () => navigate('/');

  const handleInviteUser = () => {
    if (!isRootUser) return;
    setIsInviteModalOpen(true);
  };
  const handleCloseInviteModal = () => setIsInviteModalOpen(false);

  const handleRemoveMember = (user) => {
    if (!isRootUser || userData?.email === user.email) return;
    const confirmed = window.confirm(`Remove ${user.username} from this workspace?`);
    if (!confirmed) return;

    setDeletingUserId(user._id);
    deleteUserMutation.mutate({ userId: user._id });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'username',
        width: '32%',
        Cell: ({ value }) => (
          <MemberNameRow>
            <MemberName>{value}</MemberName>
          </MemberNameRow>
        ),
      },
      {
        Header: 'Email',
        accessor: 'email',
        width: '32%',
        Cell: ({ value }) => <EmailText>{value}</EmailText>,
      },
      {
        Header: 'Joined',
        accessor: 'createdAt',
        width: '18%',
        Cell: ({ value }) => <JoinedText>{formatDate(value)}</JoinedText>,
      },
      {
        Header: 'Actions',
        accessor: '_id',
        width: '18%',
        disableSortBy: true,
        Cell: ({ row }) => {
          const user = row.original;
          const isDeletingThis = deletingUserId === user._id;
          const isSelf = userData?.email === user.email;
          const isDeleteDisabled = !isRootUser || isSelf;
          const deleteTitle = !isRootUser
            ? 'Only the root user can remove team members'
            : isSelf
              ? 'You cannot remove your own account'
              : `Remove ${user.username}`;

          return (
            <RowActionsGroup>
              <RowActionButton
                type="button"
                $danger
                title={deleteTitle}
                aria-label={deleteTitle}
                onClick={() => !isDeleteDisabled && handleRemoveMember(user)}
                disabled={isDeletingThis || isDeleteDisabled}
                style={isDeleteDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
              >
                {isDeletingThis ? <Loader2 data-spin="true" /> : <Trash2 />}
              </RowActionButton>
            </RowActionsGroup>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deletingUserId, userData]
  );

  return (
    <TeamMembersWrapper>
      <TeamMembersContainer>
        <PageHeader>
          <HeaderLeft>
            <Title>Team members</Title>
            <Description>People with access to this support workspace.</Description>
          </HeaderLeft>
          <ActionButtons>
            <OutlineButon onClick={handleDashboard} type="button">
              Dashboard
            </OutlineButon>
            {isRootUser && (
              <InviteUserButton type="button" onClick={handleInviteUser}>
                <PlusIcon size={16} />
                Invite user
              </InviteUserButton>
            )}
          </ActionButtons>
        </PageHeader>

        <TableCard>
          <TableToolbar>
            <SearchSlot>
              <SearchBar
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search members by name or email…"
              />
            </SearchSlot>
          </TableToolbar>

          {isLoading ? (
            <EmptyState>
              <SpinWrapper>
                <Loader2 />
              </SpinWrapper>
              Loading team members...
            </EmptyState>
          ) : isError ? (
            <EmptyState>
              Failed to load team members. Please try again later.
              <RetryButton type="button" onClick={() => refetch()} disabled={isFetching}>
                <RotateCcw />
                {isFetching ? 'Retrying…' : 'Retry'}
              </RetryButton>
            </EmptyState>
          ) : (
            <>
              <Table
                data={filteredUsers}
                columns={columns}
                hasFooter
                page={page - 1}
                pageSize={PAGE_SIZE}
                total={total}
                isLoading={isLoading}
                isFetching={isFetching}
                prevPage={() => setPage((p) => Math.max(p - 1, 1))}
                nextPage={() => setPage((p) => p + 1)}
                emptyStateText="No team members match your search"
              />
            </>
          )}
        </TableCard>
      </TeamMembersContainer>

      {isRootUser && (
        <InviteUserModal isOpen={isInviteModalOpen} onClose={handleCloseInviteModal} />
      )}
    </TeamMembersWrapper>
  );
};

export default TeamMembers;
